import { useLocation, useNavigate } from "react-router-dom";

import { Button, Tooltip } from "@fronterahealth/frontera-ui-components";

import { FormSavingIndicator } from "@components/ReportField/FormSavingIndicator";
import { useHasUnsavedFields } from "@components/ReportField/useHasUnsavedFields";
import { notifyError } from "@components/notifications/notifications";
import { EvaluationSubRoute, getNextRoute, getPastRoute } from "@pages/EvaluationDetails/EvaluationDetails";

interface FooterButtonRowProps {
  primaryButtonTitle?: string;
  primaryButtonLoading?: boolean;
  primaryButtonDisabled?: boolean;
  primaryButtonType?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  primaryButtonAction?: () => Promise<unknown>;
  primaryButtonDisabledTooltipReason?: string;
  stickyToBottom?: boolean;
  skipNavigate?: boolean;
  hideBackButton?: boolean;
  dataTestID?: string;
  leadingActions?: React.ReactNode;
}
export const FooterButtonRow: React.FC<FooterButtonRowProps> = ({
  primaryButtonAction,
  primaryButtonType = "button",
  primaryButtonTitle = "Next Step",
  primaryButtonDisabled = false,
  primaryButtonDisabledTooltipReason = "",
  primaryButtonLoading,
  stickyToBottom = true,
  skipNavigate = false,
  hideBackButton = false,
  dataTestID = "footer-primary-button",
  leadingActions,
}) => {
  const { pathname } = useLocation();
  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as EvaluationSubRoute) || ("upload-files" as EvaluationSubRoute);
  const nextRoute = getNextRoute(currentRoute);
  const pastRoute = getPastRoute(currentRoute);

  const hasUnsavedFields = useHasUnsavedFields();
  const go = useNavigate();

  return (
    <div
      id="btnRow"
      // NOTE: 288 comes from the width of the sidebar expanded, 72px from that width of the sidebar collapsed
      className={`-ml-12 flex w-full items-center justify-end border-t border-limestone-100 px-8 ${stickyToBottom ? "fixed bottom-0" : ""}  z-10 bg-white py-[11.5px]`}
    >
      <div className="flex items-center gap-2 flex-1">{leadingActions}</div>
      <div className="flex items-center gap-2">
        <FormSavingIndicator />
        {!hideBackButton ? (
          <Button
            appearance="secondary"
            text="Back"
            onClick={() => {
              if (pastRoute) {
                window.scrollTo({ top: 0 });
                go(`../${pastRoute}`);
              }
            }}
          />
        ) : (
          <></>
        )}
        <Tooltip
          text={primaryButtonDisabledTooltipReason}
          showInfoIcon={false}
          disabled={!primaryButtonDisabled || !primaryButtonDisabledTooltipReason}
        >
          <Button
            dataTestID={dataTestID}
            appearance="primary"
            disabled={primaryButtonDisabled || hasUnsavedFields}
            text={primaryButtonLoading ? "Loading..." : primaryButtonTitle}
            type={primaryButtonType}
            onClick={
              primaryButtonType === "submit"
                ? undefined
                : async () => {
                    if (nextRoute) {
                      window.scrollTo({ top: 0 });
                      if (primaryButtonAction) {
                        try {
                          await primaryButtonAction();
                          if (!skipNavigate) {
                            go(`../${nextRoute}`);
                          }
                        } catch (_err) {
                          console.error("Error calling primaryButtonAction from FooterButtonRow");
                          notifyError("Failed to perform action");
                          return;
                        }
                      } else {
                        if (!skipNavigate) {
                          window.scrollTo({ top: 0 });
                          go(`../${nextRoute}`);
                        }
                      }
                    }
                  }
            }
          />
        </Tooltip>
      </div>
    </div>
  );
};
